import { useState, useEffect, useCallback, useRef } from 'react';
import {
  CookieConsent,
  getStoredConsent,
  getDefaultConsent,
  saveConsent,
  acceptAllCookies,
  rejectNonEssentialCookies,
  hasConsented,
  CONSENT_UPDATED_EVENT,
} from '@/lib/cookieConsent';
import { analytics, updateConsent as updateGTMConsent, pushToDataLayer } from '@/lib/analytics';
import { optInPostHog, optOutPostHog } from '@/lib/analytics/posthog-init';

export type ConsentVariant = 'control' | 'soft-wall' | 'progressive-nudge';

interface UseCookieConsentReturn {
  consent: CookieConsent;
  showBanner: boolean;
  hasConsented: boolean;
  acceptAll: () => void;
  rejectNonEssential: () => void;
  updateConsent: (updates: Partial<Omit<CookieConsent, 'essential' | 'timestamp' | 'version'>>) => void;
  openPreferences: () => void;
  closePreferences: () => void;
  showPreferences: boolean;
  variant: ConsentVariant;
}

function syncConsentToGTM(consent: CookieConsent) {
  updateGTMConsent({
    analytics_storage: consent.analytics ? 'granted' : 'denied',
    ad_storage: consent.marketing ? 'granted' : 'denied',
    ad_user_data: consent.marketing ? 'granted' : 'denied',
    ad_personalization: consent.marketing ? 'granted' : 'denied',
    functionality_storage: 'granted',
    personalization_storage: consent.personalization ? 'granted' : 'denied',
  });
}

export const useCookieConsent = (variant: ConsentVariant = 'control'): UseCookieConsentReturn => {
  const [consent, setConsent] = useState<CookieConsent>(getDefaultConsent);
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const bannerShownAt = useRef<number | null>(null);

  useEffect(() => {
    const stored = getStoredConsent();
    if (stored) {
      setConsent(stored);
      setShowBanner(false);
      syncConsentToGTM(stored);
      if (stored.analytics) {
        optInPostHog();
      } else {
        optOutPostHog();
      }
    } else {
      const timeout = setTimeout(() => {
        // REK-3389: consent may have been granted meanwhile (implied login
        // consent / account restore / signup checkbox) -- never show the
        // banner over an existing decision.
        if (getStoredConsent()) return;
        bannerShownAt.current = Date.now();
        setShowBanner(true);
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, []);

  // Sync state when consent is set externally (signup-implied analytics
  // consent, account restore, signup marketing checkbox). REK-3389: the
  // dispatcher already applied GTM Consent Mode + PostHog (see
  // `dispatchConsentUpdated`), so this listener only updates React state and
  // hides the banner -- re-syncing here would send a duplicate consent update.
  useEffect(() => {
    function onExternalUpdate() {
      const stored = getStoredConsent();
      if (stored) {
        setConsent(stored);
        setShowBanner(false);
        setShowPreferences(false);
      }
    }
    window.addEventListener(CONSENT_UPDATED_EVENT, onExternalUpdate);
    return () => window.removeEventListener(CONSENT_UPDATED_EVENT, onExternalUpdate);
  }, []);

  const trackDecision = useCallback((decision: 'accept_all' | 'reject_non_essential' | 'customize', analyticsGranted: boolean) => {
    const elapsed = bannerShownAt.current ? Date.now() - bannerShownAt.current : 0;
    const props = { decision, variant, time_to_decision_ms: elapsed, trigger: 'banner' as const };

    // Always push to GTM dataLayer (essential storage, no consent needed)
    pushToDataLayer('consent_decision', props);

    // For accept/customize-with-analytics: opt PostHog in first so the event is captured
    if (analyticsGranted) {
      optInPostHog();
      analytics.trackEvent({ name: 'consent_decision', properties: props });
    }
  }, [variant]);

  const acceptAll = useCallback(() => {
    const newConsent = acceptAllCookies();
    trackDecision('accept_all', true);
    setConsent(newConsent);
    setShowBanner(false);
    setShowPreferences(false);
    syncConsentToGTM(newConsent);
  }, [trackDecision]);

  const rejectNonEssential = useCallback(() => {
    const newConsent = rejectNonEssentialCookies();
    trackDecision('reject_non_essential', false);
    setConsent(newConsent);
    setShowBanner(false);
    setShowPreferences(false);
    syncConsentToGTM(newConsent);
    optOutPostHog();
  }, [trackDecision]);

  const updateConsentHandler = useCallback((
    updates: Partial<Omit<CookieConsent, 'essential' | 'timestamp' | 'version'>>
  ) => {
    const newConsent: CookieConsent = {
      ...consent,
      ...updates,
      essential: true,
      timestamp: new Date().toISOString(),
      version: consent.version,
      source: 'explicit',
    };
    trackDecision('customize', newConsent.analytics);
    saveConsent(newConsent);
    setConsent(newConsent);
    setShowBanner(false);
    setShowPreferences(false);
    syncConsentToGTM(newConsent);
    if (!newConsent.analytics) {
      optOutPostHog();
    }
  }, [consent, trackDecision]);

  const openPreferences = useCallback(() => {
    setShowPreferences(true);
  }, []);

  const closePreferences = useCallback(() => {
    setShowPreferences(false);
  }, []);

  return {
    consent,
    showBanner,
    hasConsented: hasConsented(),
    acceptAll,
    rejectNonEssential,
    updateConsent: updateConsentHandler,
    openPreferences,
    closePreferences,
    showPreferences,
    variant,
  };
};
